import { DEFAULT_SHIPPING_CONFIG, type CatalogOverrides, type Cents, type GlassVariant, type Product, type ShippingConfig, autoArticleNumber, effectivePricePerM2, isDisabled } from "@glastotaal/core";
import { groepPad } from "../groep-url.mjs";
import { glassSoortImage, GLASS_PHOTO_PLACEHOLDER } from "@/lib/glass-photos";
import settingsJson from "../../../data/seed/settings.json";
import shippingSeed from "../../../data/seed/shipping.json";
import { groepRank, SUB_ORDER, SUB2_ORDER } from "./timmers-order";
import { groepOverrideRank, orderColors } from "./catalog-order";
import { relevanceScore, EMPTY_POPULARITY } from "@/lib/relevance";

/** Groep-rang met handmatige override (catalog-order.ts) vóór de Timmers-volgorde. */
function groepRankOv(category: string, groep: string): number {
  const ov = groepOverrideRank(category, groep);
  return ov === -1 ? groepRank(category, groep) : ov;
}

// Build-time seed (Timmers Floatglas). De DATA (products + articles, ~1,8 MB bron) staat
// in ./data.ts en wordt DYNAMISCH geladen via loadCatalogData() hieronder — main.tsx wacht
// erop vóór de eerste render, zodat het parsen niet in de kritieke opstart-taak valt
// (grootste Total Blocking Time-post). De sync-accessors hieronder blijven ongewijzigd;
// vóór de load geven ze lege lijsten terug (komt in de praktijk niet voor: alles wat ze
// aanroept rendert pas ná de load).
let products: Product[] = [];
let articles: Article[] = [];

let catalogDataPromise: Promise<void> | null = null;
/** Laadt de catalogus-seed (idempotent). main.tsx await dit vóór de eerste render;
 *  Node-scripts en tests moeten het zelf één keer aanroepen. */
export function loadCatalogData(): Promise<void> {
  if (!catalogDataPromise) {
    catalogDataPromise = import("./data").then((m) => {
      products = m.products;
      articles = m.articles;
    });
  }
  return catalogDataPromise;
}

/** A fixed-price article (glasbeslag/benodigdheden). Price excl. BTW, in cents. */
export interface Article {
  slug: string;
  name: string;
  category: string;
  /** Echte thuisafdeling; alleen gezet op multi-rubriek-views waar `category` de rubriek is. */
  homeCategory?: string;
  groep: string;
  /** Niveau-2 subcategorie (sub-subgroep), optioneel — voor het 2-niveau menu. */
  subgroep?: string;
  /** Profielen: verkoopwijze uit de Timmers-omschrijving (zie profielVerkoop in core). */
  perM1?: boolean;
  handelslengteMm?: number;
  opMaatMogelijk?: boolean;
  /** Leverancier zegt "Afname per handelslengte": alleen hele staven, zie length-article.ts. */
  alleenHandelslengte?: boolean;
  /** Niveau-3 subcategorie (sub-sub-subgroep), optioneel — voor 4-niveau-menu. */
  sub2groep?: string;
  /** Alle rubriek-lidmaatschappen (incl. primair), als het artikel onder meerdere
   *  categorieën hoort — zo verschijnt het in álle bijbehorende rubrieken. */
  memberships?: { category: string; groep: string; subgroep?: string; sub2groep?: string }[];
  articleNumber: string;
  /** Merk/leverancier (uit de Timmers-groepen) — voor de merk-filter. */
  merk?: string;
  /** Kleur/afwerking (NL) — voor de kleur-filter. */
  color?: string;
  priceCents: Cents;
  // Geen `timmersDiscountPct` hier: onze dealerkorting hoort niet in de publieke bundel
  // (met de publieke verkoopprijs erbij is de inkoopprijs anders zo terug te rekenen).
  // De admin haalt de percentages op via de callable `supplierDiscounts`.
  /** True = "prijs op aanvraag": geen online prijs, niet bestelbaar, offerte-CTA. */
  onRequest?: boolean;
  /** Laagste prijs van een configureerbaar artikel (rooster/voorzetraam) — voor "vanaf €X". */
  vanafCents?: number;
  /** Timmers "op voorraad" (momentopname bij sync). False = op bestelling. */
  inStock?: boolean;
  /** Aantal op eigen voorraad bij Timmers (voor "nog X op voorraad"-signaal). */
  stock?: number;
  image?: string;
  /** Aantal varianten (incl. dit artikel), bij producten met meerdere uitvoeringen. */
  variantCount?: number;
  /** Klikbare variant-tegels: variant-slug + label + prijs + voorraad + product-fototje (image), hex als terugval. */
  variantThumbs?: { slug: string; label: string; priceCents: number; inStock?: boolean; image?: string; hex?: string }[];
  /** Soort variatie: "kleur" (swatches), "maat" of generiek "variant". */
  variantKind?: "kleur" | "maat" | "variant";
}
/**
 * Eigen ("logische") artikelnummers `GT-<CAT>-<NNN>`, deterministisch en stabiel:
 * per primaire categorie gesorteerd op het originele Timmers-nummer, daarna 1-based
 * doorgenummerd. Zo verschuift een nummer niet als een ándere categorie wijzigt, en
 * blijft het klantnummer los van de Timmers-nummering (die alleen op de inkoop telt).
 *
 * Lazy + memoized: deze sort-over-alle-artikelen draaide vroeger meteen bij het laden
 * van de module (dus op élke pagina, ook de homepage) — nu pas bij de eerste échte
 * aanroep (productpagina/bestelbon), wat op de meeste pagina's nooit gebeurt.
 */
let autoArticleNrCache: Map<string, string> | null = null;
function getAutoArticleNr(): Map<string, string> {
  if (autoArticleNrCache) return autoArticleNrCache;
  const byCat = new Map<string, Article[]>();
  for (const a of articles) {
    const arr = byCat.get(a.category) ?? [];
    arr.push(a);
    byCat.set(a.category, arr);
  }
  const map = new Map<string, string>();
  for (const [cat, arr] of byCat) {
    arr
      .slice()
      .sort((x, y) => (x.articleNumber || x.slug).localeCompare(y.articleNumber || y.slug, "nl", { numeric: true }))
      .forEach((a, i) => map.set(a.slug, autoArticleNumber(cat, i + 1)));
  }
  autoArticleNrCache = map;
  return map;
}

/** Eigen artikelnummer (GT-…) van een artikel; terugval op het Timmers-nummer als het ontbreekt. */
export function ownArticleNumber(slug: string, fallback = ""): string {
  return getAutoArticleNr().get(slug) ?? fallback;
}

export function getArticles(): Article[] {
  return articles;
}
export function getArticle(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug);
}
export function getArticlesByCategory(categorySlug: string): Article[] {
  const out: Article[] = [];
  for (const a of articles) {
    const eigen = a.category === categorySlug;
    // Multi-rubriek: élke plek waar Timmers dit artikel in DEZE rubriek toont, krijgt een VIEW
    // met de groep/subgroep van die plek (zodat het in de juiste tak van de boom valt). Dat zijn
    // er soms twee binnen één rubriek — dubbelzijdige tape hoort zowel bij Banden en Tapes als
    // bij Spiegelbenodigdheden, en een handkitpistool zowel bij Kitten › Toebehoren als bij
    // Gereedschappen › Handdruk kitpistolen. Eerder won de eerste plek en ontbrak het artikel op
    // de tweede: van de 7 handkitpistolen waren er daar nog 3 te zien (03-10-2026).
    const plekken = (a.memberships ?? []).filter((x) => x.category === categorySlug);
    // De eigen (primaire) plek staat als eerste in `memberships`; zonder dat veld is er maar één.
    if (eigen && plekken.length === 0) { out.push(a); continue; }
    for (const m of plekken) {
      // `homeCategory` blijft de ECHTE afdeling van het artikel: in de view wordt `category`
      // overschreven naar de rubriek waarin het meelift, en zonder dit veld is een meeliftend
      // artikel niet meer te onderscheiden van het eigen assortiment (zie relevantie/upsells).
      const zelfdePlek = eigen && m.groep === a.groep && (m.subgroep ?? "") === (a.subgroep ?? "");
      out.push(zelfdePlek ? a : { ...a, category: m.category, homeCategory: a.category, groep: m.groep, subgroep: m.subgroep, sub2groep: m.sub2groep });
    }
  }
  return out;
}

/** Seed shipping config, with the standard defaults filling any missing field. */
export function getShippingConfig(): ShippingConfig {
  const s = (settingsJson as { shipping?: Partial<ShippingConfig> }).shipping ?? {};
  // shipping.json komt uit de admin (scripts/build-admin-seed.mjs draait vóór de build). Die
  // wint van de code-standaard, want dít is wat een crawler op een voorgerenderde pagina ziet —
  // een Firestore-read doet hij niet. Zie het scriptcommentaar voor de afkeuring die dat opleverde.
  return { ...DEFAULT_SHIPPING_CONFIG, ...s, ...(shippingSeed as Partial<ShippingConfig>) };
}

/** Default levertijd-indicatie glas op maat (admin-editable; per soort te verfijnen).
 *  Realistische basis voor maatwerk-glas — admin overschrijft globaal én per soort. */
export const DEFAULT_LEAD_TIME_TEXT = "10–15 werkdagen";

/** Seed levertijd-indicatie, with the standard default as fallback. */
export function getLeadTimeText(): string {
  const t = (settingsJson as { leadTimeText?: string }).leadTimeText;
  return typeof t === "string" && t.trim() ? t : DEFAULT_LEAD_TIME_TEXT;
}

/** Schakelaar: slijpwerk-toeslag (%) voor niet-rechtlijnige modellen (0 = uit). */
export function getSlijpwerkVormToeslagPct(): number {
  const v = (settingsJson as { slijpwerkVormToeslagPct?: number }).slijpwerkVormToeslagPct;
  return typeof v === "number" && v > 0 ? v : 0;
}

const unique = <T,>(xs: T[]): T[] => Array.from(new Set(xs));

export function getProducts(): Product[] {
  return products;
}

export function getProduct(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}

export function getVariant(
  productSlug: string,
  variantId: string,
): GlassVariant | undefined {
  return getProduct(productSlug)?.variants?.find((v) => v.id === variantId);
}

/** A "soort glas" the customer picks first (a product subcategory). */
export interface Soort {
  productSlug: string;
  productName: string;
  subcategory: string;
  img?: string;
  /** Lowest sale rate in this soort, €/m² excl. BTW, in cents. */
  vanafCents: Cents;
}

/** URL slug for a soort, e.g. "Spiegels op maat" → "spiegels-op-maat". */
export function soortSlug(s: Pick<Soort, "subcategory">): string {
  return s.subcategory
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Resolve a soort from its URL slug (first match wins on the rare collision). */
export function findSoortBySlug(slug: string): Soort | undefined {
  return getSoorten().find((s) => soortSlug(s) === slug);
}

/** All glass types across products, for the overview page (kies je glassoort). */
export function getSoorten(): Soort[] {
  const out: Soort[] = [];
  for (const p of products) {
    const variants = p.variants ?? [];
    const subs = p.subcategories?.length
      ? p.subcategories
      : unique(variants.map((v) => v.subcategory ?? ""));
    for (const sub of subs) {
      const vs = variants.filter((v) => v.subcategory === sub);
      if (vs.length === 0) continue;
      out.push({
        productSlug: p.slug,
        productName: p.name,
        subcategory: sub,
        // Eigen soort-foto → anders de foto van de eerste uitvoering → anders de placeholder
        // (nooit de API-afbeelding, die draagt het Timmers-logo).
        img: glassSoortImage(soortSlug({ subcategory: sub }), orderColors(sub, vs.map((v) => v.color ?? "").filter(Boolean))),
        vanafCents: Math.min(...vs.map((v) => v.pricePerM2)),
      });
    }
  }
  return out;
}

/**
 * Soorten mét de live admin-overrides toegepast: uitgeschakelde soorten/varianten vallen weg
 * en de "vanaf"-prijs volgt de prijsfactor/-override. Lege overrides = identiek aan getSoorten().
 * (Pure functie; de hook `useSoorten` levert 'm reactief.)
 */
export function soortenWithOverrides(ov: CatalogOverrides): Soort[] {
  const out: Soort[] = [];
  for (const p of products) {
    const variants = p.variants ?? [];
    const subs = p.subcategories?.length ? p.subcategories : unique(variants.map((v) => v.subcategory ?? ""));
    for (const sub of subs) {
      const slug = soortSlug({ subcategory: sub });
      if (isDisabled(slug, ov)) continue; // hele soort uit
      const vs = variants.filter((v) => v.subcategory === sub && !isDisabled(v.id, ov));
      if (vs.length === 0) continue; // alle varianten uit → soort verbergen
      out.push({
        productSlug: p.slug,
        productName: p.name,
        subcategory: sub,
        img: glassSoortImage(slug, orderColors(sub, vs.map((v) => v.color ?? "").filter(Boolean))), // eigen foto → eerste uitvoering mét foto → placeholder
        vanafCents: Math.min(...vs.map((v) => effectivePricePerM2(v.pricePerM2, v.id, ov, [sub, p.slug]))) as Cents,
      });
    }
  }
  return out;
}

/**
 * Overrides aangevuld met €0-geprijsde items in `disabled`, mits `hideZeroPriced` aanstaat.
 * Doel: als een sync-fout een prijs op €0 zet, verdwijnt dat product automatisch uit de winkel
 * (i.p.v. "gratis" verkoopbaar te zijn). Uitgezonderd: "op aanvraag"-artikelen en configureerbare
 * artikelen mét vanaf-prijs (voorzetramen/roosters/U-profielen) — die zijn met opzet €0 in
 * `priceCents`. Puur client-side zichtbaarheid, net als de handmatige `disabled`-set.
 */
export function withZeroPricedDisabled(ov: CatalogOverrides): CatalogOverrides {
  if (!ov?.hideZeroPriced) return ov;
  const extra: string[] = [];
  for (const p of products) {
    for (const v of p.variants ?? []) {
      if (effectivePricePerM2(v.pricePerM2, v.id, ov, []) === 0) extra.push(v.id);
    }
  }
  for (const a of getArticles()) {
    if (a.onRequest || (a.vanafCents ?? 0) > 0) continue; // met opzet €0 → niet verbergen
    if (effectivePricePerM2(a.priceCents, a.slug, ov, []) === 0) extra.push(a.slug);
    // Óók de kleur-/maat-VARIANTEN van een artikel: een variant met €0 (sync-gat) mag
    // niet als "gratis" uitvoering blijven staan. De variant-slug gaat in `disabled`.
    for (const t of a.variantThumbs ?? []) {
      if (effectivePricePerM2(t.priceCents, t.slug, ov, []) === 0) extra.push(t.slug);
    }
  }
  if (extra.length === 0) return ov;
  return { ...ov, disabled: [...new Set([...(ov.disabled ?? []), ...extra])] };
}

/** Zichtbare varianten van een artikel: de variant-thumbs minus de uitgeschakelde (bv. €0). */
export function visibleThumbs(article: Article, ov: CatalogOverrides | null | undefined): NonNullable<Article["variantThumbs"]> {
  const thumbs = article.variantThumbs ?? [];
  if (!ov?.disabled?.length) return thumbs;
  return thumbs.filter((t) => !isDisabled(t.slug, ov));
}

/** A light summary of a child category (for a parent overzichtspagina). */
export interface CategoryChild {
  slug: string;
  name: string;
  intro: string;
  count: number;
  image?: string;
}

/** A top-level category for the category-nav + /categorie pages. */
export interface CategoryView {
  slug: string;
  name: string;
  intro: string;
  /** True when the category has live products/subcategorieën; false = "binnenkort". */
  available: boolean;
  /** Parent category slug, if this is a subcategorie (e.g. balustrade → glasbeslag). */
  parent?: string;
  /** Glass soorten in this category (configurable glas). */
  soorten: Soort[];
  /** Fixed-price articles in this category (glasbeslag/benodigdheden). */
  articles: Article[];
  /** Subcategorieën, for an overkoepelende categorie (Glasbeslag). */
  children: CategoryChild[];
}

/** Menu groep — bepaalt het blok in het menu-overzicht. */
export type CategoryGroup = "glas" | "beslag";

interface CategoryMeta {
  slug: string;
  name: string;
  group: CategoryGroup;
  /** Links a category to its glass product(s) (soorten). Eén categorie kan
   *  meerdere producten bundelen (Enkel glas toont ook gekleurd glas). */
  productSlugs?: string[];
  /** Koppelt op subcategorie-naam i.p.v. product (bv. Veiligheidsglas = Gehard/Gelaagd/…
   *  binnen het enkel-glas-product). */
  subcategories?: string[];
  /** Overkoepelende categorie waar dit een subcategorie van is. */
  parent?: string;
  intro: string;
}

// Hele categorie-taxonomie. Blok "glas" = glas op maat (nadruk), blok "beslag"
// = Timmers-artikelen. Glasbeslag is een overkoepelende categorie (parent) voor
// Balustrade / Vitrine- en meubelbeslag / Interieurbeglazing.
const CATEGORY_META: CategoryMeta[] = [
  // — Glas op maat — (Enkel glas-rail bundelt veiligheidsglas, enkel glas, spiegels & gekleurd glas)
  { slug: "enkel-glas", name: "Enkel glas", group: "glas", productSlugs: ["enkel-glas", "gekleurd-glas"], intro: "Enkel glas, veiligheidsglas, gekleurd glas en spiegels — op de millimeter op maat gemaakt, in elke dikte." },
  { slug: "veiligheidsglas", name: "Veiligheidsglas", group: "glas", subcategories: ["Gehard glas", "Gelaagd glas", "Gehard-gelaagd glas", "Beloopbaar glas"], intro: "Gehard, gelaagd, gehard-gelaagd en beloopbaar glas — veilig glas op maat." },
  { slug: "gekleurd-glas", name: "Gekleurd glas", group: "glas", productSlugs: ["gekleurd-glas"], subcategories: ["Geëmailleerd glas", "Gehard gelakt glas"], intro: "Lacobel, Matelac, geëmailleerd en gelakt glas — strakke gekleurde glaspanelen op maat." },
  { slug: "installatieglas", name: "Installatieglas", group: "glas", intro: "Glazen deuren op maat — installatieglas met uitsparingen voor het beslag." },
  { slug: "klep-val-draairamen", name: "Klep-, val- en draairamen", group: "glas", intro: "Beglazing voor te openen klep-, val- en draairamen — op maat, met de juiste dikte en bewerkingen." },
  { slug: "isolatieglas", name: "Isolatieglas", group: "glas", intro: "Dubbel- en triple-glas (HR++/HR+++) voor isolatie en geluidsreductie." },
  { slug: "vacuumglas", name: "Vacuümglas", group: "glas", intro: "Ultradun vacuümglas met topisolatie — ideaal voor monumenten en renovatie." },
  { slug: "douche-en-bad", name: "Douche & Bad", group: "glas", intro: "Doucheglas-beslag, scharnieren, stabilisatiestangen en alles voor de badkamer." },
  // — Beslag & artikelen — (1-op-1 Timmers-categorieën in Timmers-volgorde; geen 'Glasbeslag'-paraplu) —
  { slug: "interieurbeglazing", name: "Interieurbeglazing", group: "beslag", intro: "Beslag en profielen voor glazen binnendeuren, steellook deuren en scheidingswanden." },
  { slug: "steellookdeuren", name: "Steellookdeuren", group: "beslag", parent: "interieurbeglazing", intro: "Steellook deuren met slanke stalen profielen — industriële look, op maat samengesteld." },
  { slug: "vitrine-en-meubelbeslag", name: "Vitrine- en meubelbeslag", group: "beslag", intro: "Beslag voor vitrinekasten en glazen meubels — scharnieren, sloten en steunen." },
  { slug: "balustrade", name: "Balustrade", group: "beslag", intro: "Balustrade-systemen, glasklemmen, handlopen en ankers." },
  { slug: "glasartikelen", name: "Glasartikelen", group: "beslag", intro: "Kit, gereedschap, glaslatten, beglazingsblokjes en alles voor de montage." },
  { slug: "ventilatieroosters", name: "Ventilatieroosters", group: "beslag", intro: "Ventilatieroosters voor glas — standaard, geluiddempend, brandwerend en hoogbouw." },
];

/** Light summaries of the subcategorieën of a parent category. */
function childrenOf(parentSlug: string): CategoryChild[] {
  return CATEGORY_META.filter((c) => c.parent === parentSlug).map((c) => {
    const arts = getArticlesByCategory(c.slug);
    return { slug: c.slug, name: c.name, intro: c.intro, count: arts.length, image: topArticleImage(c.slug) };
  });
}

/** Resolve a category view by slug (undefined when the slug is unknown). */
export function getCategory(slug: string): CategoryView | undefined {
  const meta = CATEGORY_META.find((c) => c.slug === slug);
  if (!meta) return undefined;
  const soorten =
    meta.productSlugs || meta.subcategories
      ? getSoorten().filter(
          (s) =>
            (meta.productSlugs?.includes(s.productSlug) ?? false) ||
            (meta.subcategories?.includes(s.subcategory) ?? false),
        )
      : [];
  const arts = getArticlesByCategory(meta.slug);
  const children = childrenOf(meta.slug);
  return {
    slug: meta.slug,
    name: meta.name,
    intro: meta.intro,
    parent: meta.parent,
    available: soorten.length > 0 || arts.length > 0 || children.some((c) => c.count > 0),
    soorten,
    articles: arts,
    children,
  };
}

/** Name of a category (for breadcrumbs/labels) without building the full view. */
export function getCategoryName(slug: string): string | undefined {
  return CATEGORY_META.find((c) => c.slug === slug)?.name;
}

/** Alle categorieën (slug + naam + blok), in menu-volgorde — voor admin-pickers e.d. */
export function getCategories(): { slug: string; name: string; group: CategoryGroup }[] {
  return CATEGORY_META.map((c) => ({ slug: c.slug, name: c.name, group: c.group }));
}

/** Een item dat uitgelicht/in de actie kan: een vaste-prijs-artikel OF een
 *  configureerbare glassoort (per m², dus alleen uitlichten — geen vaste korting). */
export interface PromotableItem {
  slug: string;
  name: string;
  /** Categorie-slug (voor het filter in de admin). */
  category: string;
  /** Korte ondertitel voor de homepage (groep of "Glas op maat"). */
  subtitle: string;
  image?: string;
  kind: "article" | "glas";
  /** Vaste prijs (excl. btw, centen) — artikel; 0 voor glas/prijs-op-aanvraag. */
  priceCents: Cents;
  /** Glas: vanaf-prijs per m² (excl. btw, centen). */
  vanafCents?: Cents;
  /** Geen vaste prijs → alleen uitlichten, geen korting (glas of prijs-op-aanvraag). */
  onRequest: boolean;
  /** Link naar de product-/configuratorpagina. */
  to: string;
}

let promotableCache: PromotableItem[] | null = null;

/** Alle uitlicht-/actie-bare producten: glassoorten + alle vaste-prijs-artikelen. */
export function getPromotableItems(): PromotableItem[] {
  if (promotableCache) return promotableCache;
  const glas: PromotableItem[] = getSoorten().map((s) => ({
    slug: soortSlug(s),
    name: s.subcategory,
    category: s.productSlug,
    subtitle: "Glas op maat",
    image: s.img,
    kind: "glas",
    priceCents: 0,
    vanafCents: s.vanafCents,
    onRequest: true,
    to: `/glas-op-maat/${soortSlug(s)}`,
  }));
  const arts: PromotableItem[] = getArticles().map((a) => ({
    slug: a.slug,
    name: a.name,
    category: a.category,
    subtitle: a.groep,
    image: a.image,
    kind: "article",
    priceCents: a.priceCents,
    vanafCents: a.vanafCents,
    onRequest: !!a.onRequest || a.priceCents <= 0,
    to: `/artikel/${a.slug}`,
  }));
  promotableCache = [...glas, ...arts];
  return promotableCache;
}

/** Eén uitlicht-/actie-baar item op slug (glassoort of artikel). */
export function getPromotableItem(slug: string): PromotableItem | undefined {
  return getPromotableItems().find((i) => i.slug === slug);
}

/** Foto van het meest relevante artikel (mét foto) in een categorie (optioneel binnen één groep). */
function topArticleImage(categorySlug: string, groep?: string): string | undefined {
  return getArticlesByCategory(categorySlug)
    .filter((a) => a.image && (!groep || a.groep === groep))
    .sort((a, b) => relevanceScore(b, b.priceCents, EMPTY_POPULARITY) - relevanceScore(a, a.priceCents, EMPTY_POPULARITY))[0]?.image;
}

/**
 * Representatieve foto voor een categorie in de nav:
 *  - artikel-/beslagcategorie → de foto van het MEEST RELEVANTE product (relevantie-score).
 *  - glascategorie → de foto van het EERSTE product (soort); dat is de placeholder als er nog
 *    geen eigen foto is (glassSoortPhoto geeft altijd minstens de placeholder terug).
 */
/** Expliciete categoriefoto's waar de auto-afleiding een ongeschikt beeld zou kiezen (bv.
 *  vacuümglas heeft geen artikel-/soortfoto → viel terug op een leeg wit render-strookje in de
 *  nav-thumb; beng-monu leest ook klein goed). */
const CATEGORY_IMAGE_OVERRIDE: Record<string, string> = {
  vacuumglas: "/glas-foto/beng-monu.webp",
};

export function getCategoryImage(slug: string, ov?: CatalogOverrides | null): string | undefined {
  if (CATEGORY_IMAGE_OVERRIDE[slug]) return CATEGORY_IMAGE_OVERRIDE[slug];
  const direct = topArticleImage(slug);
  if (direct) return direct;
  const cat = getCategory(slug);
  if (!cat) return undefined;
  if (cat.soorten.length) {
    // Sla soorten/uitvoeringen over die in de admin uitstaan (alleen mogelijk als de aanroeper de
    // overrides meegeeft) én soorten die nog geen échte foto hebben — anders houdt één soort zonder
    // eigen foto de hele nav-tegel op de placeholder, ook nadat je 'm hebt uitgezet.
    const live = ov
      ? soortenWithOverrides(ov).filter((s) => cat.soorten.some((c) => soortSlug(c) === soortSlug(s)))
      : cat.soorten;
    const metFoto = live.find((s) => s.img && s.img !== GLASS_PHOTO_PLACEHOLDER);
    return metFoto?.img ?? live[0]?.img ?? cat.soorten[0]?.img;
  }
  for (const ch of cat.children) {
    const img = topArticleImage(ch.slug);
    if (img) return img;
  }
  return undefined;
}

/** Productgroepen (subgroepen) binnen een artikelcategorie, in Timmers-volgorde
 *  (webshopSortIndex); niet-gerangschikte groepen (merk-buckets) achteraan op aantal. */
export function getCategoryGroepen(categorySlug: string, limit = 999): { groep: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const a of getArticlesByCategory(categorySlug)) counts.set(a.groep, (counts.get(a.groep) ?? 0) + 1);
  return [...counts.entries()]
    .map(([groep, count]) => ({ groep, count }))
    .sort((a, b) => groepRankOv(categorySlug, a.groep) - groepRankOv(categorySlug, b.groep) || b.count - a.count || a.groep.localeCompare(b.groep, "nl"))
    .slice(0, limit);
}

/**
 * Elke groep- en subgroeppagina als één zoekbaar item.
 *
 * Waarom. De zoekbalk kende alleen de zes hoofdcategorieën en de losse producten. Zocht iemand op
 * "kit", dan kwamen er wel een Kitmes en een kitpistool bovendrijven, maar niet de groep **Kitten**
 * met alle siliconen-, acrylaat- en beglazingskitten erin — terwijl dat precies is wat je zoekt.
 * Diezelfde groepen hebben sinds de SEO-slag al een eigen adres (groepPad), dus er is niets nieuws
 * te bouwen: ze moesten alleen nog in de zoekindex.
 *
 * Lege (sub)groepen blijven eruit: een treffer die naar nul producten leidt is erger dan geen
 * treffer. De foto is die van het meest relevante artikel uit de groep, zodat de suggestieregel
 * er hetzelfde uitziet als een product.
 */
export interface GroepPagina {
  /** Stabiele sleutel voor de zoekindex. */
  id: string;
  /** "Kitten" of "Siliconenkit". */
  label: string;
  /** Categorienaam eronder, bv. "Glasartikelen" — plus de groep bij een subgroep. */
  pad: string;
  href: string;
  image?: string;
  count: number;
}
export function getGroepPaginas(): GroepPagina[] {
  const out: GroepPagina[] = [];
  for (const cat of getCategories()) {
    const catNaam = cat.name;
    for (const node of getCategoryTree(cat.slug)) {
      if (!node.groep || node.count === 0) continue;
      out.push({
        id: `groep-${cat.slug}-${node.groep}`,
        label: node.groep,
        pad: catNaam,
        href: groepPad(cat.slug, node.groep),
        image: topArticleImage(cat.slug, node.groep),
        count: node.count,
      });
      for (const sub of node.subs) {
        if (!sub.label || sub.count === 0) continue;
        out.push({
          id: `sub-${cat.slug}-${node.groep}-${sub.label}`,
          label: sub.label,
          pad: `${catNaam} · ${node.groep}`,
          href: groepPad(cat.slug, node.groep, sub.label),
          image: subgroepImage(cat.slug, node.groep, sub.label),
          count: sub.count,
        });
      }
    }
  }
  return out;
}

/** Foto van het meest relevante artikel binnen een subgroep. */
function subgroepImage(categorySlug: string, groep: string, subgroep: string): string | undefined {
  return getArticlesByCategory(categorySlug)
    .filter((a) => a.image && a.groep === groep && a.subgroep === subgroep)
    .sort((a, b) => relevanceScore(b, b.priceCents, EMPTY_POPULARITY) - relevanceScore(a, a.priceCents, EMPTY_POPULARITY))[0]?.image;
}

/** 2-niveau boom van een artikelcategorie: groep → sub-subgroepen (met aantallen). */
export interface MenuTreeNode {
  groep: string;
  count: number;
  subs: { label: string; count: number; sub2s: { label: string; count: number }[] }[];
}
export function getCategoryTree(categorySlug: string): MenuTreeNode[] {
  const byGroep = new Map<string, { count: number; subs: Map<string, { count: number; sub2s: Map<string, number> }> }>();
  for (const a of getArticlesByCategory(categorySlug)) {
    const g = byGroep.get(a.groep) ?? { count: 0, subs: new Map<string, { count: number; sub2s: Map<string, number> }>() };
    g.count += 1;
    if (a.subgroep) {
      const sg = g.subs.get(a.subgroep) ?? { count: 0, sub2s: new Map<string, number>() };
      sg.count += 1;
      if (a.sub2groep) sg.sub2s.set(a.sub2groep, (sg.sub2s.get(a.sub2groep) ?? 0) + 1);
      g.subs.set(a.subgroep, sg);
    }
    byGroep.set(a.groep, g);
  }
  return [...byGroep.entries()]
    .map(([groep, v]) => {
      // Toon de VOLLEDIGE Timmers-sub-taxonomie (ook subgroepen zonder artikelen,
      // count 0 → "binnenkort"), aangevuld met eventuele data-only subs erachter.
      const order = SUB_ORDER[`${categorySlug} › ${groep}`] ?? [];
      const extra = [...v.subs.keys()].filter((s) => !order.includes(s));
      return {
        groep,
        count: v.count,
        subs: [...order, ...extra].map((label) => {
          const sgData = v.subs.get(label) ?? { count: 0, sub2s: new Map<string, number>() };
          const sub2Order = SUB2_ORDER[`${categorySlug} › ${groep} › ${label}`] ?? [];
          const sub2Extra = [...sgData.sub2s.keys()].filter((s) => !sub2Order.includes(s));
          return {
            label,
            count: sgData.count,
            sub2s: [...sub2Order, ...sub2Extra].map((s2) => ({ label: s2, count: sgData.sub2s.get(s2) ?? 0 })),
          };
        }),
      };
    })
    // Groepen in Timmers-volgorde (webshopSortIndex); merk-buckets achteraan op aantal.
    .sort((a, b) => groepRankOv(categorySlug, a.groep) - groepRankOv(categorySlug, b.groep) || b.count - a.count || a.groep.localeCompare(b.groep, "nl"));
}

/** Een kolom/sectie in het mega-menu. `soon` = binnenkort (nog geen data). */
export interface MegaLink {
  label: string;
  to: string;
  soon?: boolean;
}
export interface MegaColumn {
  title?: string;
  titleTo?: string;
  soon?: boolean;
  /** Representatieve foto voor de compacte (tegel-)weergave in de navbalk-dropdown. */
  image?: string;
  links: MegaLink[];
}

// Extra "binnenkort"-items die nog niet in de Timmers-feed zitten, per categorie als sectie
// toegevoegd aan het mega-menu. Alleen voor dingen zónder eigen (sub)categorie — Steellookdeuren
// stond hier én als echte subcategorie, en verscheen daardoor twee keer onder Interieurbeglazing.
const EXTRA_COLUMNS: Record<string, MegaColumn[]> = {
  ventilatieroosters: [{ title: "Hoogbouw ventilatieroosters", titleTo: "/categorie/ventilatieroosters", soon: true, links: [] }],
};

/** Rechterpaneel-inhoud voor een categorie in het mega-menu:
 *  - parent (Glasbeslag) → kolom per subcategorie met haar topgroepen
 *  - artikelcategorie → 2 niveaus: kolom per groep, met sub-subgroepen eronder */
export function getMegaColumns(slug: string): MegaColumn[] {
  const cat = getCategory(slug);
  if (!cat) return [];
  if (cat.children.some((c) => c.count > 0)) {
    return cat.children
      .filter((c) => c.count > 0)
      .map((ch) => ({
        title: ch.name,
        titleTo: `/categorie/${ch.slug}`,
        image: ch.image,
        links: getCategoryGroepen(ch.slug, 8)
          .map((g) => ({ label: g.groep, to: groepPad(ch.slug, g.groep) }) as MegaLink)
          .concat((EXTRA_COLUMNS[ch.slug] ?? []).map((col) => ({ label: col.title ?? "", to: col.titleTo ?? `/categorie/${ch.slug}`, soon: true }))),
      }));
  }
  if (cat.articles.length) {
    const cols: MegaColumn[] = getCategoryTree(slug).map((n) => ({
      title: n.groep,
      titleTo: groepPad(slug, n.groep),
      image: topArticleImage(slug, n.groep),
      links: n.subs.map((s) => ({
        label: s.label,
        // Lege Timmers-subgroep → "binnenkort", link naar de groep i.p.v. een leeg filter.
        to: s.count ? groepPad(slug, n.groep, s.label) : groepPad(slug, n.groep),
        soon: s.count === 0,
      })),
    }));
    return cols.concat(EXTRA_COLUMNS[slug] ?? []);
  }
  return EXTRA_COLUMNS[slug] ?? [];
}

/**
 * Link naar de productpagina van een artikel — ook als het een kleurvariant is.
 *
 * Een variant heeft een eigen artikelnummer en prijs, maar lang niet altijd een eigen pagina:
 * de meeste zijn een kleurstaal bij het basisartikel en openen via `?variant=`. Zonder deze
 * omweg linkte een variantkaart naar een pagina die niet bestaat. De map bouwen we één keer uit
 * de catalogus die toch al in het geheugen staat (de variant-index is een aparte, grote chunk).
 */
let variantBaseMap: Map<string, string> | null = null;

export function articleHref(slug: string): string {
  if (getArticle(slug)) return `/artikel/${slug}`;
  if (!variantBaseMap) {
    variantBaseMap = new Map();
    for (const a of getArticles()) {
      for (const v of a.variantThumbs ?? []) if (!variantBaseMap.has(v.slug)) variantBaseMap.set(v.slug, a.slug);
    }
  }
  const base = variantBaseMap.get(slug);
  return base ? `/artikel/${base}?variant=${encodeURIComponent(slug)}` : `/artikel/${slug}`;
}
